import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { ArrowsClockwise } from "@phosphor-icons/react";

const ACTIONS = [
  ["approve", "Approve"], ["reject", "Reject"], ["pickup_scheduled", "Schedule pickup"],
  ["picked_up", "Mark picked up"], ["received", "Mark received"], ["quality_ok", "QC passed"],
  ["replacement_dispatched", "Dispatch replacement"], ["closed", "Mark delivered"],
];

export default function Exchanges() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [tab, setTab] = useState("cases");

  const load = async () => {
    try {
      const [{ data }, { data: p }] = await Promise.all([api.get("/exchanges"), api.get("/exchanges/policy")]);
      setRows(data); setPolicy(p);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const doAction = async (action, extra = {}) => {
    try {
      const { data } = await api.post(`/exchanges/${selected._id}/action`, { action, ...extra });
      toast.success("Exchange updated");
      setSelected({ ...selected, status: data.status });
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const savePolicy = async () => {
    try { await api.put("/exchanges/policy", policy); toast.success("Policy saved"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="exchanges-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Exchange management" description="Exchange-only policy. Refunds are only permitted in exceptional cases with approval." tint="rose" icon={ArrowsClockwise} />
      <div className="tab-list mb-6">
        {[["cases", "Cases"], ["policy", "Policy"]].map(([t, l]) => (
          <button key={t} onClick={()=>setTab(t)} data-testid={`ex-tab-${t}`} className={tab === t ? "is-active" : ""}>{l}</button>
        ))}
      </div>

      {tab === "cases" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>Case</th><th>Order</th><th>Customer</th><th>Reason</th><th>Status</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} data-testid={`ex-row-${r.exchange_number}`}>
                  <td className="font-mono text-xs">{r.exchange_number}</td>
                  <td className="font-mono text-xs">{r.order_number || "—"}</td>
                  <td>{r.customer?.name}</td>
                  <td className="text-sm capitalize">{(r.reason||"").replace(/_/g," ")}</td>
                  <td><StatusChip value={r.status}/></td>
                  <td className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="text-right whitespace-nowrap"><button className="btn-ghost btn-sm" onClick={()=>setSelected(r)} data-testid={`ex-open-${r.exchange_number}`}>Manage</button><RecordActions entity="exchanges" record={r} frozen={["exchange_number"]} onChanged={load} /></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7}><EmptyState icon={ArrowsClockwise} title="No exchange cases yet" description="Use the Website Simulator to raise a test case." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "policy" && policy && (
        <div className="card-soft max-w-2xl">
          <div className="label-eyebrow mb-1">Exchange policy</div>
          <div className="display-3 mb-6">Configure rules</div>
          <div className="grid grid-cols-2 gap-5">
            <label className="block"><span className="label-eyebrow">Eligibility days</span>
              <input type="number" value={policy.eligibility_days} onChange={(e)=>setPolicy({...policy, eligibility_days: Number(e.target.value)})}
                     className="input-underline" data-testid="policy-days"/></label>
            <label className="block"><span className="label-eyebrow">Max exchanges / order</span>
              <input type="number" value={policy.max_exchanges_per_order} onChange={(e)=>setPolicy({...policy, max_exchanges_per_order: Number(e.target.value)})}
                     className="input-underline" data-testid="policy-max"/></label>
          </div>
          <div className="mt-6 space-y-3">
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" className="w-4 h-4 accent-foreground" checked={policy.reverse_pickup} onChange={(e)=>setPolicy({...policy, reverse_pickup: e.target.checked})} data-testid="policy-reverse"/> Reverse pickup enabled</label>
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" className="w-4 h-4 accent-foreground" checked={policy.self_shipping_allowed} onChange={(e)=>setPolicy({...policy, self_shipping_allowed: e.target.checked})} data-testid="policy-self"/> Self-shipping allowed</label>
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" className="w-4 h-4 accent-foreground" checked={policy.discounted_eligible} onChange={(e)=>setPolicy({...policy, discounted_eligible: e.target.checked})}/> Discounted products eligible</label>
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" className="w-4 h-4 accent-foreground" checked={policy.international_eligible} onChange={(e)=>setPolicy({...policy, international_eligible: e.target.checked})}/> International orders eligible</label>
          </div>
          <label className="block mt-6"><span className="label-eyebrow">Condition requirement</span>
            <input value={policy.condition_requirement} onChange={(e)=>setPolicy({...policy, condition_requirement: e.target.value})}
                   className="input-underline" data-testid="policy-condition"/></label>
          <button onClick={savePolicy} className="btn-primary mt-8" data-testid="policy-save">Save policy</button>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-40 modal-backdrop flex fade-in" onClick={()=>setSelected(null)}>
          <div className="ml-auto w-full max-w-lg h-full bg-card border-l border-border overflow-y-auto p-7 drawer-slide" onClick={(e)=>e.stopPropagation()} data-testid="ex-drawer">
            <div className="label-eyebrow">Exchange case</div>
            <div className="display-2 mt-2">{selected.exchange_number}</div>
            <div className="mt-4"><StatusChip value={selected.status}/></div>
            <div className="mt-8 space-y-4 text-sm">
              <div><span className="label-eyebrow">Customer</span><div className="mt-1.5">{selected.customer?.name} · {selected.customer?.email}</div></div>
              <div><span className="label-eyebrow">Item</span><div className="font-mono text-xs mt-1.5">{selected.item?.sku} · {selected.item?.size} {selected.item?.colour}</div></div>
              <div><span className="label-eyebrow">Reason</span><div className="capitalize mt-1.5">{(selected.reason||"").replace(/_/g," ")}</div></div>
              {selected.written_explanation && <div><span className="label-eyebrow">Explanation</span><p className="text-sm leading-relaxed mt-1.5">{selected.written_explanation}</p></div>}
              {(selected.product_photos || []).concat(selected.damage_photos||[]).slice(0,3).length > 0 && (
                <div>
                  <span className="label-eyebrow">Evidence</span>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[...(selected.product_photos||[]), ...(selected.damage_photos||[])].slice(0,6).map((url, i) => (
                      <img key={i} src={url} alt="" className="aspect-square object-cover rounded-md border border-border"/>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-8">
              <div className="label-eyebrow mb-3">Actions</div>
              <div className="grid grid-cols-2 gap-2">
                {ACTIONS.map(([a, l]) => (
                  <button key={a} onClick={()=>doAction(a)} className="btn-ghost btn-sm" data-testid={`ex-action-${a}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="mt-8">
              <div className="label-eyebrow mb-3">History</div>
              <ol className="space-y-2 text-sm border-l border-border pl-4">
                {selected.status_history?.map((h,idx)=>(
                  <li key={idx} className="flex items-center gap-3"><StatusChip value={h.status}/> <span className="text-xs text-muted-foreground">{fmtDate(h.at)}</span></li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
